import { createSelector } from '@reduxjs/toolkit';

import { parseHandlePairId } from '../../utils';
import { selectLabwareDependencyEdges } from '../graph/selectors';
import { selectLabwareIds } from '../workflow/selectors';

export type LabwareAndSourceTaskByTaskId = Record<
  string,
  Array<{
    labwareId: string;
    sourceTaskId: string | null;
    sourceTaskIds?: string[];
  }>
>;

export const selectLabwareAndSourceTaskByTaskId = createSelector(
  selectLabwareIds,
  selectLabwareDependencyEdges,
  (labwareIds, labwareDependencyEdges): LabwareAndSourceTaskByTaskId => {
    const result: LabwareAndSourceTaskByTaskId = {};

    for (const labwareId of labwareIds) {
      const startEdges = labwareDependencyEdges.filter(
        (edge) => edge.source === labwareId
      );
      if (startEdges.length === 0) {
        continue;
      }

      type State = { nodeId: string; handlePairId: string | null };
      const queue: State[] = [];
      const visited = new Set<string>();

      const perTaskAggregation: Record<string, Map<string, Set<string>>> = {};

      const seenStartPairs = new Set<string>();
      for (const e of startEdges) {
        const pair =
          typeof e.sourceHandle === 'string'
            ? parseHandlePairId(e.sourceHandle)
            : null;
        const key = pair ?? '__null__';
        if (!seenStartPairs.has(key)) {
          seenStartPairs.add(key);
          queue.push({ nodeId: labwareId, handlePairId: pair });
        }
      }

      while (queue.length > 0) {
        const { nodeId, handlePairId } = queue.shift()!;
        const visitKey = `${nodeId}::${handlePairId ?? ''}`;
        if (visited.has(visitKey)) continue;
        visited.add(visitKey);

        const nextEdges = labwareDependencyEdges.filter((edge) => {
          const handleMatches =
            typeof edge.sourceHandle === 'string'
              ? parseHandlePairId(edge.sourceHandle) === handlePairId
              : handlePairId === null;
          return edge.source === nodeId && handleMatches;
        });

        for (const nextEdge of nextEdges) {
          const taskId = nextEdge.target;
          const nextHandlePairId = nextEdge.targetHandle
            ? parseHandlePairId(nextEdge.targetHandle)
            : null;

          if (!result[taskId]) {
            result[taskId] = [];
          }

          if (!perTaskAggregation[taskId]) {
            perTaskAggregation[taskId] = new Map();
          }

          const srcTaskId = labwareIds.includes(nodeId) ? null : nodeId;

          if (srcTaskId === null) {
            // Ensure an entry exists with null source when directly from labware
            const existing = result[taskId].find(
              (e) => e.labwareId === labwareId
            );
            if (!existing) {
              result[taskId].push({ labwareId, sourceTaskId: null });
            } else if (existing.sourceTaskId !== null) {
              existing.sourceTaskId = null;
              delete existing.sourceTaskIds;
            }
          } else {
            const map = perTaskAggregation[taskId];
            if (!map.has(labwareId)) map.set(labwareId, new Set());
            map.get(labwareId)!.add(srcTaskId);
          }

          queue.push({ nodeId: taskId, handlePairId: nextHandlePairId });
        }
      }

      for (const [taskId, map] of Object.entries(perTaskAggregation)) {
        for (const [lbId, set] of map.entries()) {
          const sources = Array.from(set);
          if (!result[taskId]) {
            result[taskId] = [];
          }
          let entry = result[taskId].find((e) => e.labwareId === lbId);
          if (!entry) {
            if (sources.length === 0) {
              result[taskId].push({ labwareId: lbId, sourceTaskId: null });
            } else if (sources.length === 1) {
              result[taskId].push({
                labwareId: lbId,
                sourceTaskId: sources[0] ?? null,
              });
            } else {
              result[taskId].push({
                labwareId: lbId,
                sourceTaskId: sources[0] ?? null,
                sourceTaskIds: sources,
              });
            }
          } else {
            if (entry.sourceTaskId === null) {
              continue;
            }
            if (sources.length === 1) {
              entry.sourceTaskId = sources[0] ?? null;
              delete entry.sourceTaskIds;
            } else if (sources.length > 1) {
              entry.sourceTaskId = sources[0] ?? null;
              entry.sourceTaskIds = sources;
            }
          }
        }
      }
    }

    return result;
  }
);
